import { useCallback, useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode } from 'react';

// A control present but temporarily inoperative says why, by pointer AND by keyboard (application baseline 1.1.0
// `05-client.md` §5.5; design system `03-components.md` §2; the owner's ruling J-D3 of 2026-10-06, "Keyboard route
// required", which closed jeeves escalation 20261005-132820 and has `app-deviations.md` §2 closed with this build). Why
// not the native `disabled` attribute: it takes the control out of the tab order and suppresses its pointer events, so
// neither hover nor focus fires and the reason cannot be reached. So the control stays focusable, carries `aria-disabled="true"`, and its
// action is suppressed here; the reason is
//   - its accessible description (`aria-describedby` → a hidden element after the control, never inside it, so it never
//     joins the name) — announced on focus;
//   - the `title` the caller puts on the control or its row — on hover;
//   - and a visible bubble, opened by a keyboard focus that rests (~300 ms, as Tooltip, 03-components §5.2) and by any
//     press of the control — a click, a tap, Enter or Space — so a sighted keyboard user and a touch user, who have no
//     hover, reach it too. It closes on blur, Escape, a pointer leaving it, or a press anywhere else.
// A control inoperative only while a write the view shows in flight is the loading state, owes no reason (AB-Q7 of
// 2026-10-05) and stays natively disabled: that is the caller's `loading`, never this.

/** Delay before a keyboard focus opens the reason (03-components §5.2: tooltips open after ~300 ms). */
export const REASON_OPEN_DELAY_MS = 300;

/** The caller's own handlers, wrapped while the control is inoperative and passed through untouched otherwise. */
export interface InoperativeHandlers<E extends HTMLElement> {
  onClick?: ((event: MouseEvent<E>) => void) | undefined;
  onFocus?: ((event: FocusEvent<E>) => void) | undefined;
  onBlur?: ((event: FocusEvent<E>) => void) | undefined;
  onKeyDown?: ((event: KeyboardEvent<E>) => void) | undefined;
  onPointerLeave?: ((event: PointerEvent<E>) => void) | undefined;
}

export interface Inoperative<E extends HTMLElement> {
  /** True while the control is inoperative for a stated reason. */
  active: boolean;
  /** The hidden description's id, for the control's `aria-describedby` (undefined while operative). */
  reasonId: string | undefined;
  /** Spread onto the control: `aria-disabled` while inoperative, and the handlers. */
  controlProps: InoperativeHandlers<E> & { 'aria-disabled'?: true };
  /** Rendered straight after the control (or the row it sits in): the bubble while open, then the hidden description. */
  reasonElements: ReactNode;
}

function focusVisible(element: Element): boolean {
  try {
    return element.matches(':focus-visible');
  } catch {
    // Why true: a browser without the selector cannot tell keyboard from pointer focus; saying why once too often is
    // better than never.
    return true;
  }
}

/** The gap between the control and its bubble: `--space-2`, the tooltip's own (components.css `.tooltip--top`). */
const BUBBLE_GAP_PX = 8;
/** The bubble keeps this far from the viewport's sides: `--space-4`, the page gutter on a phone. */
const VIEWPORT_EDGE_PX = 16;

/**
 * Places the bubble above the element before it (the control, or its row) — below it when there is no room above —
 * within the viewport's width. Why measured and absolute rather than a wrapper: wrapping the control would change every
 * layout it sits in, and would remount it — dropping focus — whenever it turned inoperative.
 */
export function placeReasonBubble(bubble: HTMLElement | null): void {
  if (!bubble) return;
  const anchor = bubble.previousElementSibling;
  const container = bubble.offsetParent;
  if (!(anchor instanceof HTMLElement) || !(container instanceof HTMLElement)) return;
  const a = anchor.getBoundingClientRect();
  const c = container.getBoundingClientRect();
  const width = bubble.offsetWidth;
  const height = bubble.offsetHeight;
  const viewport = document.documentElement.clientWidth;
  const left = a.left + width > viewport - VIEWPORT_EDGE_PX ? Math.max(VIEWPORT_EDGE_PX, viewport - VIEWPORT_EDGE_PX - width) : a.left;
  const top = a.top - BUBBLE_GAP_PX - height >= 0 ? a.top - BUBBLE_GAP_PX - height : a.bottom + BUBBLE_GAP_PX;
  bubble.style.left = `${left - c.left - container.clientLeft + container.scrollLeft}px`;
  bubble.style.top = `${top - c.top - container.clientTop + container.scrollTop}px`;
}

/**
 * `reasonId` names the hidden description where the caller already derives its ids from one of its own (Checkbox's
 * `<id>-reason`); otherwise one is made.
 */
export function useInoperative<E extends HTMLElement>(reason: string | undefined, handlers: InoperativeHandlers<E>, givenReasonId?: string): Inoperative<E> {
  const id = useId();
  const reasonId = givenReasonId ?? `${id}-reason`;
  const active = reason !== undefined && reason !== '';
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);
  const control = useRef<E | null>(null);

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);
  const close = useCallback(() => {
    clear();
    setOpen(false);
  }, [clear]);

  // Why: a control that becomes operative again drops its bubble with its reason.
  useEffect(() => {
    if (!active) close();
  }, [active, close]);
  useEffect(() => clear, [clear]);

  const shown = active && open;
  // A press anywhere else closes it — the only close a tap has, since a tapped button may never take focus.
  useEffect(() => {
    if (!shown) return undefined;
    const onPointerDown = (event: globalThis.PointerEvent): void => {
      const target = event.target;
      if (target instanceof Node && control.current?.contains(target)) return;
      close();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [shown, close]);

  if (!active) {
    return { active: false, reasonId: undefined, controlProps: handlers, reasonElements: null };
  }

  const controlProps: Inoperative<E>['controlProps'] = {
    'aria-disabled': true,
    // Why every press is refused here and none reaches the caller: the action is suppressed (§5.5). `preventDefault`
    // also stops a submit button submitting its form, an implicit submission included, and a checkbox toggling.
    onClick: (event) => {
      event.preventDefault();
      control.current = event.currentTarget;
      clear();
      setOpen(true);
    },
    onFocus: (event) => {
      handlers.onFocus?.(event);
      control.current = event.currentTarget;
      if (!focusVisible(event.currentTarget)) return;
      clear();
      timer.current = window.setTimeout(() => setOpen(true), REASON_OPEN_DELAY_MS);
    },
    onBlur: (event) => {
      handlers.onBlur?.(event);
      close();
    },
    onKeyDown: (event) => {
      if (event.key === 'Escape' && open) {
        // Why stopped: Escape closes the bubble first, as it closes a tooltip, before it closes the dialog around it.
        event.stopPropagation();
        close();
        return;
      }
      handlers.onKeyDown?.(event);
    },
    onPointerLeave: (event) => {
      handlers.onPointerLeave?.(event);
      // Why a mouse only: a finger lifting off a tapped control leaves it too, and would close what the tap opened.
      if (event.pointerType === 'mouse') close();
    },
  };

  return {
    active: true,
    reasonId,
    controlProps,
    reasonElements: (
      <>
        {shown ? <span className="inoperative-reason" aria-hidden="true" ref={placeReasonBubble}>{reason}</span> : null}
        <span id={reasonId} hidden>{reason}</span>
      </>
    ),
  };
}
